// src/pages/admin/ShiftList.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSnackbar from "components/SoftSnackbar";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";

import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import DataTable from "components/QuestDataTable";

import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";

const parseStoredPermissions = () => {
  try {
    const stored = localStorage.getItem("permissions");
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const ShiftList = () => {
  const { permissions = [], userPermission = [], isAuthReady, user: authUser } = useAuth();
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [deletingId, setDeletingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedShift, setSelectedShift] = useState(null);
  const [filters, setFilters] = useState({
    q: "",
    branch_id: "",
    subbranch_id: "",
    type: "",
    is_active: "",
    owner: "",
  });
  const [branchOptions, setBranchOptions] = useState([]);
  const [subBranchOptions, setSubBranchOptions] = useState([]);

  const navigate = useNavigate();

  const grantedPermissions = useMemo(
    () => Array.from(new Set([...permissions, ...userPermission, ...parseStoredPermissions()])),
    [permissions, userPermission]
  );

  const hasShiftPermission = useCallback(
    (...requiredPermissions) => {
      if (grantedPermissions.includes("access.system.full_access")) return true;
      return requiredPermissions
        .flat()
        .filter(Boolean)
        .some((permission) => grantedPermissions.includes(permission));
    },
    [grantedPermissions]
  );
  const canUseOwnerFilter = authUser?.actor_type === "user" && Number(authUser?.vendor_id || 0) <= 0;

  const canViewPage = hasShiftPermission("shift.pages.list.view");
  const canCreate =
    hasShiftPermission("shift.pages.create.view") &&
    hasShiftPermission("shift.actions.open_create");
  const canOpenTrashed =
    hasShiftPermission("shift.pages.trashed.view") &&
    hasShiftPermission("shift.actions.open_trashed");
  const canOpenEdit =
    hasShiftPermission("shift.pages.edit.view") &&
    hasShiftPermission("shift.actions.open_edit");
  const canDelete = hasShiftPermission("shift.actions.delete");
  const canConfirmDelete = hasShiftPermission("shift.actions.confirm_delete");
  const canCancelDelete = hasShiftPermission("shift.actions.cancel_delete");
  const canViewField = useCallback(
    (field) => hasShiftPermission(`shift.fields.${field}.view`),
    [hasShiftPermission]
  );

  const queryString = useCallback(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (key === "owner" && !canUseOwnerFilter) return;
      if (value !== undefined && value !== null && value !== "") {
        params.set(key, value);
      }
    });
    return params.toString();
  }, [canUseOwnerFilter, filters]);

  useEffect(() => {
    if (canUseOwnerFilter || !filters.owner) return;
    setFilters((prev) => ({ ...prev, owner: "" }));
  }, [canUseOwnerFilter, filters.owner]);

  const fetchShifts = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const qs = queryString();
      const res = await fetch(`${API_URL}/shifts${qs ? `?${qs}` : ""}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to fetch shifts");
      }
      const { data } = await res.json();
      setShifts(data || []);
    } catch (error) {
      setSnackbar({ open: true, message: error.message, color: "error" });
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  const loadBranchOptions = useCallback(async () => {
    if (branchOptions.length > 0) return;
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/shifts/branch-options`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setBranchOptions(Array.isArray(data?.data) ? data.data : []);
    } catch {
      setBranchOptions([]);
    }
  }, [branchOptions.length]);

  const loadSubBranchOptions = useCallback(async () => {
    if (!filters.branch_id || subBranchOptions.length > 0) return;
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/shifts/sub-branch-options/${filters.branch_id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setSubBranchOptions(Array.isArray(data?.data) ? data.data : []);
    } catch {
      setSubBranchOptions([]);
    }
  }, [filters.branch_id, subBranchOptions.length]);

  useEffect(() => {
    if (!isAuthReady) return;
    if (!canViewPage) {
      setLoading(false);
      return;
    }
    fetchShifts();
  }, [canViewPage, fetchShifts, isAuthReady]);

  const formatTime = (time) => (time ? `${time.split(":")[0]}:${time.split(":")[1]}` : "N/A");
  const formatDuration = (minutes) =>
    minutes ? `${Math.floor(minutes / 60)}h ${minutes % 60}m` : "N/A";

  const handleDeleteClick = (shift) => {
    if (!canDelete) {
      setSnackbar({ open: true, message: "You do not have permission to delete shifts.", color: "error" });
      return;
    }
    setSelectedShift(shift);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (shift) => {
    if (!canOpenEdit) {
      setSnackbar({ open: true, message: "You do not have permission to edit shifts.", color: "error" });
      return;
    }
    navigate(`/shifts/edit/${shift.id}`);
  };

  const handleDeleteConfirm = async () => {
    if (!canDelete || !canConfirmDelete) {
      setSnackbar({ open: true, message: "You do not have permission to confirm delete.", color: "error" });
      return;
    }

    if (!selectedShift) return;

    try {
      setDeletingId(selectedShift.id);
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/shifts/${selectedShift.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || "Delete failed");
      }
      setSnackbar({ open: true, message: "Shift deleted successfully", color: "success" });
      await fetchShifts();
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || "Delete failed",
        color: "error",
      });
    } finally {
      setDeletingId(null);
      setDeleteDialogOpen(false);
      setSelectedShift(null);
    }
  };

  const closeDeleteDialog = () => {
    if (!canCancelDelete) return;
    setDeleteDialogOpen(false);
    setSelectedShift(null);
  };

  const handleFilterChange = (field) => (event) => {
    const value = event.target.value;
    if (field === "branch_id") {
      setSubBranchOptions([]);
    }
    setFilters((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "branch_id") {
        next.subbranch_id = "";
      }
      return next;
    });
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem("authToken");
      const qs = queryString();
      const res = await fetch(`${API_URL}/shifts/export${qs ? `?${qs}` : ""}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const error = await res.json().catch(() => ({}));
        throw new Error(error.message || "Export failed");
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "shifts.csv";
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setSnackbar({ open: true, message: error.message || "Export failed", color: "error" });
    }
  };

  const columns = [];
  if (canViewField("branch")) columns.push({ Header: "Branch", accessor: "branch" });
  if (canViewField("sub_branch")) columns.push({ Header: "Sub Branch", accessor: "sub_branch" });
  if (canViewField("shift_code")) columns.push({ Header: "Shift Code", accessor: "shift_code" });
  if (canViewField("type")) columns.push({ Header: "Type", accessor: "type" });
  if (canViewField("start_time")) columns.push({ Header: "Start Time", accessor: "start_time" });
  if (canViewField("end_time")) columns.push({ Header: "End Time", accessor: "end_time" });
  if (canViewField("has_flexible_break")) {
    columns.push({ Header: "Flexible Break", accessor: "has_flexible_break" });
  }
  if (canViewField("break_duration")) {
    columns.push({ Header: "Break Duration", accessor: "break_duration" });
  }
  if (canViewField("grace_time")) columns.push({ Header: "Grace Time (min)", accessor: "grace_time" });
  if (canViewField("expected_hours")) columns.push({ Header: "Expected Hours", accessor: "expected_hours" });
  if (canViewField("is_active")) columns.push({ Header: "Status", accessor: "is_active" });
  if (canViewField("actions") && (canOpenEdit || canDelete)) {
    columns.push({ Header: "Actions", accessor: "actions", disableSortBy: true });
  }

  const rows = shifts.map((shift) => ({
    branch: shift.branch?.name || shift.branch_name || "N/A",
    sub_branch: shift.sub_branch?.name || shift.sub_branch_name || "N/A",
    shift_code: shift.shift_code || "N/A",
    type: shift.type || "N/A",
    start_time: formatTime(shift.start_time),
    end_time: formatTime(shift.end_time),
    has_flexible_break: shift.has_flexible_break ? "Yes" : "No",
    break_duration: formatDuration(shift.break_duration),
    grace_time: `${shift.grace_time || 0} min`,
    expected_hours: shift.expected_hours ? `${shift.expected_hours / 60}h` : "N/A",
    is_active: (
      <SoftBox display="inline-flex" alignItems="center" px={1.5} py={0.5} borderRadius={1}>
        <SoftTypography variant="caption" fontWeight="bold" color={shift.is_active ? "info" : "error"}>
          {shift.is_active ? "ACTIVE" : "INACTIVE"}
        </SoftTypography>
      </SoftBox>
    ),
    actions: (
      <SoftBox display="flex" justifyContent="center">
        {canOpenEdit && (
          <Tooltip title="Edit Shift">
            <IconButton size="small" onClick={() => handleEditClick(shift)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canDelete && (
          <Tooltip title="Delete Shift">
            <IconButton size="small" color="error" onClick={() => handleDeleteClick(shift)}>
              {deletingId === shift.id ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <DeleteIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </SoftBox>
    ),
  }));

  if (!isAuthReady || (canViewPage && loading)) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="200px">
          <CircularProgress size={50} color="info" />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Shift List
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to view shifts.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Shift Management"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar({ ...snackbar, open: false })}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        time={3000}
      />

      <SoftBox p={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftTypography variant="h4" fontWeight="bold">
            Shift List
          </SoftTypography>
          <SoftBox display="flex" gap={1}>
            {canOpenTrashed && (
              <SoftButton
                size="small"
                color="warning"
                onClick={() => navigate("/shifts/trashed")}
                startIcon={<Icon>delete</Icon>}
              >
                Trash
              </SoftButton>
            )}
            <SoftButton
              size="small"
              color="dark"
              variant="outlined"
              onClick={handleExport}
              startIcon={<Icon>download</Icon>}
            >
              Export
            </SoftButton>
            {canCreate && (
              <SoftButton
                size="small"
                color="info"
                onClick={() => navigate("/shifts/add")}
                startIcon={<Icon>add</Icon>}
              >
                Add New Shift
              </SoftButton>
            )}
          </SoftBox>
        </SoftBox>

        <SoftBox display="flex" gap={1.5} flexWrap="wrap" alignItems="center" mb={2}>
          <SoftBox width={{ xs: "100%", sm: "200px" }}>
            <SoftInput
              size="small"
              placeholder="Search shifts"
              value={filters.q}
              onChange={handleFilterChange("q")}
            />
          </SoftBox>
          <select
            value={filters.branch_id}
            onFocus={loadBranchOptions}
            onChange={handleFilterChange("branch_id")}
            style={{ minHeight: 38, minWidth: 170, border: "1px solid #d2d6da", borderRadius: 6, padding: "0 10px" }}
          >
            <option value="">All Branches</option>
            {branchOptions.map((branch) => (
              <option key={branch.id} value={branch.id}>
                {branch.name}
              </option>
            ))}
          </select>
          <select
            value={filters.subbranch_id}
            onFocus={loadSubBranchOptions}
            onChange={handleFilterChange("subbranch_id")}
            disabled={!filters.branch_id}
            style={{ minHeight: 38, minWidth: 190, border: "1px solid #d2d6da", borderRadius: 6, padding: "0 10px" }}
          >
            <option value="">All Sub Branches</option>
            {subBranchOptions.map((subBranch) => (
              <option key={subBranch.id} value={subBranch.id}>
                {subBranch.name}
              </option>
            ))}
          </select>
          <select
            value={filters.type}
            onChange={handleFilterChange("type")}
            style={{ minHeight: 38, minWidth: 140, border: "1px solid #d2d6da", borderRadius: 6, padding: "0 10px" }}
          >
            <option value="">All Types</option>
            <option value="day">Day</option>
            <option value="night">Night</option>
            <option value="afternoon">Afternoon</option>
            <option value="evening">Evening</option>
          </select>
          <select
            value={filters.is_active}
            onChange={handleFilterChange("is_active")}
            style={{ minHeight: 38, minWidth: 140, border: "1px solid #d2d6da", borderRadius: 6, padding: "0 10px" }}
          >
            <option value="">All Status</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
          {canUseOwnerFilter && (
            <select
              value={filters.owner}
              onChange={handleFilterChange("owner")}
              style={{ minHeight: 38, minWidth: 180, border: "1px solid #d2d6da", borderRadius: 6, padding: "0 10px" }}
            >
              <option value="">All Owners</option>
              <option value="admin">Admin</option>
              <option value="vendor">Internal Vendor</option>
            </select>
          )}
        </SoftBox>

        {columns.length > 0 ? (
          <DataTable isSorted table={{ columns, rows }} />
        ) : (
          <SoftBox p={3}>
            <SoftTypography variant="body2" color="text">
              No visible fields are assigned for this page.
            </SoftTypography>
          </SoftBox>
        )}
      </SoftBox>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2">
            Are you sure you want to delete the shift <strong>{selectedShift?.shift_code}</strong>?
          </SoftTypography>
        </DialogContent>
        <DialogActions>
          {canConfirmDelete && (
            <SoftButton
              color="info"
              variant="contained"
              onClick={handleDeleteConfirm}
              disabled={deletingId === selectedShift?.id}
              sx={{
                backgroundColor: "rgb(23, 193, 232)",
                "&:hover": { backgroundColor: "rgba(23, 193, 232, 0.8)" },
                color: "#fff",
                minWidth: 100,
              }}
            >
              {deletingId === selectedShift?.id ? "Processing..." : "Confirm"}
            </SoftButton>
          )}
          {canCancelDelete && (
            <SoftButton
              variant="contained"
              onClick={closeDeleteDialog}
              sx={{
                backgroundColor: "red",
                "&:hover": { backgroundColor: "darkred" },
                color: "#fff",
                minWidth: 100,
              }}
            >
              Cancel
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default ShiftList;
